// Modal for adding/editing a raw material
import { useState, useEffect } from "react";
import { useAuth } from "../../../hooks/useAuth";
import { useTheme } from "../../CommonComponents/ThemeProvider";
import FileUploadField from "../../CommonComponents/FileUploadField";
import tinycolor from "tinycolor2";

export default function AddRawMaterialModal({
    isOpen,
    onClose,
    onSubmit,
    editData,
}) {
    const { customerId } = useAuth();
    const uploadsBaseURL = `${process.env.REACT_APP_API_URL}/uploads`;
    const { currentTheme } = useTheme();
    const primaryColor = currentTheme.color;
    const hoverColor = tinycolor(primaryColor).darken(10).toString();

    const [materialData, setMaterialData] = useState({
        code: "",
        name: "",
        unit_type: "",
        description: "",
        image: null,
        customer_id: null,
    });

    const [preview, setPreview] = useState(null);
    const [imageRemoved, setImageRemoved] = useState(false);

    /*
        Sync modal state when editing OR adding
    */
    useEffect(() => {
        if (editData) {
            setMaterialData({
                code: editData.code || "",
                name: editData.name || "",
                unit_type: editData.unit_type || "",
                description: editData.description || "",
                image: null, // Never preload file input
                customer_id: editData.customer_id || customerId || null,
            });
            const previewUrl = editData.image_url
                ? (String(editData.image_url).startsWith("http")
                    ? editData.image_url
                    : `${uploadsBaseURL}/${editData.image_url}`)
                : null;
            setPreview(previewUrl);
            setImageRemoved(false);
        } else {
            setMaterialData({
                code: "",
                name: "",
                unit_type: "",
                description: "",
                image: null,
                customer_id: customerId || null,
            });
            setPreview(null);
            setImageRemoved(false);
        }
    }, [editData, customerId]);

    /*
        Handle text inputs
    */
    const handleChange = (e) => {
        const { name, value } = e.target;
        setMaterialData((prev) => ({ ...prev, [name]: value }));
    };

    /*
        Handle image selection
    */
    const handleImageChange = (file) => {
        setMaterialData((prev) => ({ ...prev, image: file }));
        setPreview(file ? URL.createObjectURL(file) : null);
        setImageRemoved(false);
    };

    /*
        Remove image
    */
    const handleImageRemove = () => {
        setMaterialData((prev) => ({ ...prev, image: null }));
        setPreview(null);
        setImageRemoved(true);
    };

    /*
        Submit form
    */
    const handleSubmit = (e) => {
        e.preventDefault();

        const formData = new FormData();
        formData.append("code", materialData.code);
        formData.append("name", materialData.name);
        formData.append("unit_type", materialData.unit_type);
        formData.append("description", materialData.description);
        formData.append("customer_id", materialData.customer_id);

        if (materialData.image) {
            formData.append("image", materialData.image);
        }
        if (imageRemoved) {
            formData.append("remove_image", "1");
        }

        onSubmit(formData);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-96 shadow-lg">
                <h2 className="text-xl font-bold mb-4">
                    {editData ? "Edit Raw Material" : "Add Raw Material"}
                </h2>

                <form onSubmit={handleSubmit} className="flex flex-col gap-3">
                    <input
                        name="name"
                        placeholder="Raw Material Name"
                        value={materialData.name}
                        onChange={handleChange}
                        className="border px-3 py-2 rounded"
                        required
                    />

                    <select
                        name="unit_type"
                        value={materialData.unit_type}
                        onChange={handleChange}
                        className="border px-3 py-2 rounded"
                        required
                    >
                        <option value="">Select unit type</option>
                        <option value="pcs">Piece</option>
                        <option value="kg">Kg</option>
                        {/* <option value="liter">Liter</option> */}
                        {/* <option value="pack">Pack</option> */}
                    </select>

                    <textarea
                        name="description"
                        placeholder="Description (optional)"
                        value={materialData.description}
                        onChange={handleChange}
                        className="border px-3 py-2 rounded"
                        rows={2}
                    />

                    <FileUploadField
                        label="Raw Material Image"
                        accept="image/*"
                        value={materialData.image}
                        preview={preview}
                        onChange={handleImageChange}
                        onRemove={handleImageRemove}
                        optional={true}
                    />

                    <div className="flex justify-end gap-2 mt-4">
                        <button
                            type="button"
                            className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                            onClick={onClose}
                        >
                            Cancel
                        </button>

                        <button
                            type="submit"
                            className="px-4 py-2 text-white rounded font-medium shadow-md transition"
                            style={{ background: primaryColor }}
                            onMouseOver={(e) =>
                                (e.currentTarget.style.background = hoverColor)
                            }
                            onMouseOut={(e) =>
                            (e.currentTarget.style.background =
                                primaryColor)
                            }
                        >
                            {editData ? "Update" : "Add"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
